import type { ApprovalScope } from '@harness/shared/protocol';
import { useState } from 'react';
import styles from '../styles/cards.module.css';
import { approvalTitle, TEXT } from '../texts';

interface ApprovalCardProps {
  tool: string;
  input: unknown;
  allowTurn: boolean;
  closed: boolean;
  onDecide: (allow: boolean, scope: ApprovalScope) => void;
}

function formatted(input: unknown): string {
  try {
    return JSON.stringify(input, null, 2) ?? '';
  } catch {
    return String(input);
  }
}

export function ApprovalCard({ tool, input, allowTurn, closed, onDecide }: ApprovalCardProps): React.JSX.Element {
  const [decision, setDecision] = useState<string | null>(null);
  if (closed || decision !== null) {
    return (
      <div className={styles.closed}>
        {approvalTitle(tool)} · {decision ?? TEXT.decided}
      </div>
    );
  }
  const decide = (allow: boolean, scope: ApprovalScope, label: string): void => {
    setDecision(label);
    onDecide(allow, scope);
  };
  return (
    <div className={allowTurn ? styles.card : styles.risky} role="group" aria-label={TEXT.approvalNeeded}>
      <div className={styles.header}>{TEXT.approvalNeeded}</div>
      <div className={styles.title}>{approvalTitle(tool)}</div>
      {!allowTurn && <div className={styles.warning}>⚠ {TEXT.riskyAction}</div>}
      <details className={styles.details}>
        <summary>{TEXT.showDetails}</summary>
        <pre>{formatted(input)}</pre>
      </details>
      <div className={styles.actions}>
        <button type="button" className={styles.primary} onClick={() => decide(true, 'once', TEXT.yes)}>
          {TEXT.yes}
        </button>
        {allowTurn && (
          <button type="button" className={styles.secondary} onClick={() => decide(true, 'turn', TEXT.yesForAnswer)}>
            {TEXT.yesForAnswer}
          </button>
        )}
        <button type="button" className={styles.danger} onClick={() => decide(false, 'once', TEXT.no)}>
          {TEXT.no}
        </button>
      </div>
    </div>
  );
}
